import type { AxiosInstance } from "axios";
import type {
  SchoolClassroomCategory,
  SchoolClassroomCategoryInput,
  SchoolClassroomCategoryQuery,
  SchoolClassroomListFilters,
} from "~/types/schoolClassroomCategories";
import HttpFactory from "~/repository/factory";
import { ENDPOINTS } from "~/repository/endpoints";
import { buildSchoolStudentListQuery } from "~/utils/schoolStudentListQuery";
import type {
  SchoolPage,
  SchoolStudentListFilters,
  SchoolStudentExportColumn,
  SchoolStudent,
  SchoolStudentQuickAccessLink,
  SchoolStudentInput,
  SchoolStudentPatch,
  SchoolStudentTrialAccess,
  SchoolStudentTrialInput,
  SchoolClassroom,
  SchoolHistoryEvent,
  SchoolEnrollmentInput,
  SchoolEnrollmentPreview,
  SchoolBulkInput,
  SchoolBulkPreview,
  SchoolImportPreview,
} from "~/types/schoolManagement";
import type {
  ClassroomReadOptions,
  ClassroomScheduleSlotResponse,
  ClassroomScheduleSlotInput,
  ClassroomScheduleSlotPatchInput,
} from "~/types/classroom";

const students = ENDPOINTS.PROFILE.MY_STUDENTS;
const classrooms = ENDPOINTS.CLASSROOM.CLASSROOMS;
const classroomCategories = "/api/organizations/current/classroom-categories/";
const imports = "/api/profile/school-student-imports/";

/** Explicit paginated school contracts keep legacy teacher array consumers intact. */
export default class SchoolManagementModule extends HttpFactory {
  constructor(axiosInstance: AxiosInstance) {
    super(axiosInstance, "school");
  }

  listStudents(filters: Partial<SchoolStudentListFilters>) {
    return this.get<SchoolPage<SchoolStudent>>(students, {
      ...filters,
      paginated: true,
    });
  }

  exportStudents(
    filters: SchoolStudentListFilters,
    columns: SchoolStudentExportColumn[],
  ) {
    const query = buildSchoolStudentListQuery(filters);
    delete query.page;
    delete query.page_size;
    return this.get<Blob>(
      `${students}export/`,
      { ...query, columns: columns.join(",") },
      { responseType: "blob" },
    );
  }

  getStudent(id: number) {
    return this.get<SchoolStudent>(`${students}${id}/`);
  }

  createStudent(input: SchoolStudentInput) {
    return this.post<SchoolStudent[]>(students, input);
  }

  patchStudent(id: number, input: SchoolStudentPatch) {
    return this.patch<SchoolStudent>(`${students}${id}/`, input);
  }

  setStudentBlocked(id: number, isBlocked: boolean) {
    return this.post<SchoolStudent>(`${students}${id}/block/`, {
      is_blocked: isBlocked,
    });
  }

  releaseStudentAccess(id: number) {
    return this.post<SchoolStudent>(`${students}${id}/release-access/`, {});
  }

  studentHistory(id: number) {
    return this.get<SchoolHistoryEvent[]>(`${students}${id}/history/`);
  }

  studentHistoryPage(id: number, page = 1, pageSize = 25) {
    return this.get<SchoolPage<SchoolHistoryEvent>>(
      `${students}${id}/history/`,
      {
        paginated: true,
        page,
        page_size: pageSize,
      },
    );
  }

  listStudentTrialAccess(id: number, page = 1, pageSize = 25) {
    return this.get<SchoolPage<SchoolStudentTrialAccess>>(
      `${students}${id}/trial-access/`,
      { pending: true, page, page_size: pageSize },
    );
  }

  grantStudentTrialAccess(id: number, input: SchoolStudentTrialInput) {
    return this.post<SchoolStudentTrialAccess>(
      `${students}${id}/trial-access/`,
      input,
    );
  }

  revokeStudentTrialAccess(id: number, trialId: number) {
    return this.post<SchoolStudentTrialAccess>(
      `${students}${id}/trial-access/${trialId}/revoke/`,
      {},
    );
  }

  studentEnrollments(id: number) {
    return this.get<
      Array<{
        id: number;
        classroom_id: number;
        classroom_name: string;
        teacher_id: number;
        teacher_name: string;
        entered_at: string | null;
        left_at: string | null;
        entry_source: string;
      }>
    >(`${students}${id}/enrollments/`);
  }

  resendAccess(id: number) {
    return this.post<{ status: string; detail: string }>(
      `${students}${id}/resend-access/`,
      {},
    );
  }

  resetStudentPassword(id: number, password: string) {
    return this.post<void>(`${students}${id}/reset-password/`, { password });
  }

  generateStudentQuickAccessLink(id: number) {
    return this.post<SchoolStudentQuickAccessLink>(
      `${students}${id}/quick-access-link/`,
      {},
    );
  }

  previewStudents(input: SchoolBulkInput) {
    return this.post<SchoolBulkPreview>(`${students}bulk-preview/`, input);
  }

  applyStudents(input: SchoolBulkInput & { preview_token: string }) {
    return this.post<{
      action: string;
      count: number;
      results: SchoolStudent[];
    }>(`${students}bulk-apply/`, input);
  }

  previewImport(file: File, classroomIds: number[]) {
    const body = new FormData();
    body.append("file", file);
    body.append("classroom_ids", JSON.stringify(classroomIds));
    return this.post<SchoolImportPreview>(`${imports}preview/`, body);
  }

  confirmImport(id: string) {
    return this.post<SchoolImportPreview>(`${imports}${id}/confirm/`, {});
  }

  listClassroomCategories(query: SchoolClassroomCategoryQuery = {}) {
    return this.get<SchoolPage<SchoolClassroomCategory>>(classroomCategories, {
      ...query,
    });
  }

  getClassroomCategory(id: number) {
    return this.get<SchoolClassroomCategory>(`${classroomCategories}${id}/`);
  }

  createClassroomCategory(input: SchoolClassroomCategoryInput) {
    return this.post<SchoolClassroomCategory>(classroomCategories, input);
  }

  patchClassroomCategory(
    id: number,
    input: Partial<SchoolClassroomCategoryInput>,
  ) {
    return this.patch<SchoolClassroomCategory>(
      `${classroomCategories}${id}/`,
      input,
    );
  }

  listClassrooms(filters: Partial<SchoolClassroomListFilters>) {
    return this.get<SchoolPage<SchoolClassroom>>(classrooms, {
      ...filters,
      uncategorized: filters.uncategorized ? true : undefined,
      paginated: true,
    });
  }

  getClassroom(id: number, options: ClassroomReadOptions = {}) {
    return this.get<SchoolClassroom>(
      `${classrooms}${id}/`,
      options.includeTextBody ? { include_text_body: true } : {},
    );
  }

  createClassroom(input: {
    name: string;
    teacher: number;
    category_id?: number | null;
  }) {
    return this.post<SchoolClassroom>(classrooms, input);
  }

  patchClassroom(
    id: number,
    input: {
      name?: string;
      teacher?: number;
      category_id?: number | null;
      expected_version: number;
    },
  ) {
    return this.patch<SchoolClassroom>(`${classrooms}${id}/`, input);
  }

  setClassroomArchived(
    id: number,
    archived: boolean,
    version: number,
    confirmStudentConflicts = false,
  ) {
    return this.post<SchoolClassroom>(
      `${classrooms}${id}/${archived ? "archive" : "restore"}/`,
      {
        expected_version: version,
        confirm_student_conflicts: confirmStudentConflicts,
      },
    );
  }

  classroomHistory(id: number) {
    return this.get<SchoolHistoryEvent[]>(`${classrooms}${id}/history/`);
  }

  previewEnrollment(id: number, input: SchoolEnrollmentInput) {
    return this.post<SchoolEnrollmentPreview>(
      `${classrooms}${id}/enrollments/preview/`,
      input,
    );
  }

  commitEnrollment(id: number, previewToken: string, confirmWarnings: boolean) {
    return this.post<{ changed_count: number }>(
      `${classrooms}${id}/enrollments/commit/`,
      { preview_token: previewToken, confirm_warnings: confirmWarnings },
    );
  }

  listSchedule(classroomId: number) {
    return this.get<ClassroomScheduleSlotResponse[]>(
      ENDPOINTS.CLASSROOM.CLASSROOM_SCHEDULE_SLOTS,
      { classroom_id: classroomId },
    );
  }

  /** Complete recurring timetable in the current school workspace. */
  listAllSchedule() {
    return this.get<ClassroomScheduleSlotResponse[]>(
      ENDPOINTS.CLASSROOM.CLASSROOM_SCHEDULE_SLOTS,
    );
  }

  createSchedule(
    input: ClassroomScheduleSlotInput & { confirm_student_conflicts?: boolean },
  ) {
    return this.post<ClassroomScheduleSlotResponse>(
      ENDPOINTS.CLASSROOM.CLASSROOM_SCHEDULE_SLOTS,
      input,
    );
  }

  updateSchedule(
    id: number,
    input: ClassroomScheduleSlotPatchInput & {
      confirm_student_conflicts?: boolean;
    },
  ) {
    return this.patch<ClassroomScheduleSlotResponse>(
      `${ENDPOINTS.CLASSROOM.CLASSROOM_SCHEDULE_SLOTS}${id}/`,
      input,
    );
  }

  deleteSchedule(id: number) {
    return this.delete<void>(
      `${ENDPOINTS.CLASSROOM.CLASSROOM_SCHEDULE_SLOTS}${id}/`,
    );
  }
}
